import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { format, addHours } from "date-fns";
import { Timestamp } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import {
  dateOnlyToUtcDate,
  dateOnlyExclusiveEndToUtcDate,
  utcExclusiveEndToInclusiveDateText,
} from "../../../../utils/recordDateTimeUtils";
import styles from "./AttributeSection.module.css";

const coerceDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime()) ? parsed : null;
  }
  if (typeof value === "object" && typeof value.toDate === "function") {
    try {
      const dateValue = value.toDate();
      return Number.isFinite(dateValue?.getTime()) ? dateValue : null;
    } catch (error) {
      return null;
    }
  }
  return null;
};

const buildDefaultLocalState = (timezone) => {
  const now = new Date();
  const oneHourLater = addHours(now, 1);

  return {
    startDate: format(now, "yyyy-MM-dd"),
    startTime: format(now, "HH:mm"),
    endDate: format(oneHourLater, "yyyy-MM-dd"),
    endTime: format(oneHourLater, "HH:mm"),
    timezone,
    isAllDay: false,
  };
};

const buildLocalStateFromRange = (range, fallbackTimezone) => {
  if (!range || !range.start || !range.end) {
    return buildDefaultLocalState(fallbackTimezone);
  }

  const timezone =
    typeof range.timezone === "string" && range.timezone.trim()
      ? range.timezone.trim()
      : fallbackTimezone;

  const startDate = coerceDateValue(range.start);
  const endDate = coerceDateValue(range.end);

  if (!startDate || !endDate) {
    return buildDefaultLocalState(timezone);
  }

  try {
    const localStart = toZonedTime(startDate, timezone);
    const localEnd = toZonedTime(endDate, timezone);
    const isAllDay = Boolean(range.isAllDay);

    if (isAllDay) {
      // Skip-aware inverse of the exclusive stored end; naive minus-one-day
      // arithmetic can land on a date the timezone skips entirely, which the
      // write path then cannot resolve.
      const inclusiveEnd = utcExclusiveEndToInclusiveDateText(
        endDate,
        timezone
      );
      if (!inclusiveEnd.ok) {
        return buildDefaultLocalState(timezone);
      }
      const startText = format(localStart, "yyyy-MM-dd");

      return {
        startDate: startText,
        startTime: "",
        endDate:
          inclusiveEnd.dateText < startText ? startText : inclusiveEnd.dateText,
        endTime: "",
        timezone,
        isAllDay,
      };
    }

    return {
      startDate: format(localStart, "yyyy-MM-dd"),
      startTime: format(localStart, "HH:mm"),
      endDate: format(localEnd, "yyyy-MM-dd"),
      endTime: format(localEnd, "HH:mm"),
      timezone,
      isAllDay,
    };
  } catch (error) {
    console.warn("Failed to build calendar state from range:", error);
    return buildDefaultLocalState(timezone);
  }
};

const areStatesEqual = (a, b) => {
  return (
    a.startDate === b.startDate &&
    a.startTime === b.startTime &&
    a.endDate === b.endDate &&
    a.endTime === b.endTime &&
    a.timezone === b.timezone &&
    a.isAllDay === b.isAllDay
  );
};

export default function CalendarAttributes({
  dateRange,
  onUpdate,
  onDelete,
  aiOverlay,
}) {
  const { t } = useTranslation();
  // Auto-detect browser timezone
  const defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Initialize local state from dateRange prop
  const [localState, setLocalState] = useState(() =>
    buildLocalStateFromRange(dateRange, defaultTimezone)
  );
  const syncingFromPropsRef = useRef(false);
  const hasOverlay = Boolean(aiOverlay);
  const isRemovalOverlay = aiOverlay?.isRemoval === true;
  const overlayDisplayState = useMemo(() => {
    if (!aiOverlay || isRemovalOverlay) {
      return null;
    }
    return buildLocalStateFromRange(aiOverlay.value, defaultTimezone);
  }, [aiOverlay, defaultTimezone, isRemovalOverlay]);
  const displayState = overlayDisplayState || localState;
  const overlaySummary = useMemo(() => {
    if (!aiOverlay) return null;
    if (isRemovalOverlay) {
      return t("records.editor.calendar.ai.clear_event", "AI will clear this event.");
    }
    if (!overlayDisplayState) {
      return t(
        "records.editor.calendar.ai.suggest_updates",
        "AI suggested updates to this event."
      );
    }
    const { startDate, startTime, endDate, endTime, timezone, isAllDay } =
      overlayDisplayState;
    if (isAllDay) {
      return t("records.editor.calendar.ai.suggest_all_day", {
        startDate,
        endDate,
        timezone,
        defaultValue: `AI suggests an all-day event from ${startDate} to ${endDate} (${timezone}).`,
      });
    }
    return t("records.editor.calendar.ai.suggest_range", {
      startDate,
      startTime,
      endDate,
      endTime,
      timezone,
      defaultValue: `AI suggests ${startDate} ${startTime} → ${endDate} ${endTime} (${timezone}).`,
    });
  }, [aiOverlay, overlayDisplayState, isRemovalOverlay, t]);

  useEffect(() => {
    if (hasOverlay) {
      syncingFromPropsRef.current = false;
      return;
    }
    const nextState = buildLocalStateFromRange(dateRange, defaultTimezone);
    setLocalState((prev) => {
      if (areStatesEqual(prev, nextState)) {
        return prev;
      }
      syncingFromPropsRef.current = true;
      return nextState;
    });
  }, [dateRange, defaultTimezone, hasOverlay]);

  useEffect(() => {
    if (hasOverlay || !syncingFromPropsRef.current) {
      return;
    }
    const expectedState = buildLocalStateFromRange(dateRange, defaultTimezone);
    if (areStatesEqual(localState, expectedState)) {
      syncingFromPropsRef.current = false;
    }
  }, [localState, dateRange, defaultTimezone, hasOverlay]);
  
  // Update parent component when local state changes
  useEffect(() => {
    if (hasOverlay || syncingFromPropsRef.current) {
      return;
    }

    const { startDate, startTime, endDate, endTime, timezone, isAllDay } = localState;
    
    // Don't update if we don't have required fields
    if (!startDate || !endDate || !timezone) return;
    
    // Validate date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(startDate) || !dateRegex.test(endDate)) {
      console.warn("Invalid date format:", { startDate, endDate });
      return;
    }
    
    // Validate time format (HH:MM) for timed events
    if (!isAllDay && (startTime || endTime)) {
      const timeRegex = /^\d{2}:\d{2}$/;
      if ((startTime && !timeRegex.test(startTime)) || (endTime && !timeRegex.test(endTime))) {
        console.warn("Invalid time format:", { startTime, endTime });
        return;
      }
    }
    
    try {
      let startDateTime, endDateTime;
      
      if (isAllDay) {
        // All-day boundaries mirror the backend canonical local-start-of-date
        // contract (functions/records/recordDateTimeUtils.js): start is the
        // canonical local start of startDate; stored end is exclusive, the
        // canonical local start of the first resolvable date after endDate.
        const startResult = dateOnlyToUtcDate(startDate, timezone);
        const endResult = dateOnlyExclusiveEndToUtcDate(endDate, timezone);
        if (!startResult.ok || !endResult.ok) {
          console.warn("Unresolvable all-day date for timezone:", {
            startDate,
            endDate,
            timezone,
          });
          return;
        }
        startDateTime = startResult.date;
        endDateTime = endResult.date;
      } else {
        // For timed events, require time values
        if (!startTime || !endTime) return;

        startDateTime = fromZonedTime(`${startDate} ${startTime}:00`, timezone);
        endDateTime = fromZonedTime(`${endDate} ${endTime}:00`, timezone);
      }

      // Validate that end is after start
      if (endDateTime <= startDateTime) return;

      // Validate timezone conversion results
      if (
        !startDateTime ||
        !endDateTime ||
        isNaN(startDateTime.getTime()) ||
        isNaN(endDateTime.getTime())
      ) {
        console.warn("Invalid datetime conversion results");
        return;
      }

      // Create DateRange object with Firestore Timestamps
      const dateRangeValue = {
        start: Timestamp.fromDate(startDateTime),
        end: Timestamp.fromDate(endDateTime),
        timezone,
        isAllDay,
      };

      // Check if the dateRange has actually changed
      if (
        dateRange &&
        dateRange.start &&
        dateRange.end &&
        dateRange.start.seconds === dateRangeValue.start.seconds &&
        dateRange.start.nanoseconds === dateRangeValue.start.nanoseconds &&
        dateRange.end.seconds === dateRangeValue.end.seconds &&
        dateRange.end.nanoseconds === dateRangeValue.end.nanoseconds &&
        dateRange.timezone === dateRangeValue.timezone &&
        dateRange.isAllDay === dateRangeValue.isAllDay
      ) {
        return; // No change, don't update
      }

      onUpdate("date", dateRangeValue);
    } catch (error) {
      console.error("Failed to process calendar date/time:", error);
      // Don't update parent on error - keep current state
    }
  }, [localState, onUpdate, dateRange, hasOverlay]);
  
  const handleFieldChange = useCallback((field, value) => {
    if (hasOverlay) return;
    setLocalState(prev => ({
      ...prev,
      [field]: value
    }));
  }, [hasOverlay]);
  
  const handleAllDayToggle = useCallback((checked) => {
    if (hasOverlay) return;
    setLocalState(prev => {
      if (!checked) {
        // When toggling off all-day, round to next hour like initial setup
        const now = new Date();
        const nextHour = new Date(now);
        nextHour.setHours(now.getHours() + 1, 0, 0, 0);
        
        return {
          ...prev,
          isAllDay: checked,
          startTime: format(nextHour, "HH:mm"),
          endTime: format(addHours(nextHour, 1), "HH:mm")
        };
      }
      
      // When toggling on all-day, clear times
      return {
        ...prev,
        isAllDay: checked,
        startTime: "",
        endTime: ""
      };
    });
  }, [hasOverlay]);
  return (
    <div
      className={`${styles.section} ${styles.active} ${
        hasOverlay ? styles.showingSuggestions : ""
      }`}
    >
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <svg
            className={styles.icon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <span>{t("records.editor.calendar.title", "Event Details")}</span>
        </div>
        <button
          className={styles.deleteButton}
          onClick={onDelete}
          aria-label={t(
            "records.editor.calendar.remove_attributes_aria",
            "Remove calendar attributes"
          )}
          disabled={hasOverlay}
          type="button"
          title={t(
            "records.editor.calendar.remove_attributes_aria",
            "Remove calendar attributes"
          )}
        >
          ×
        </button>
      </div>

      {hasOverlay && (
        <div
          className={
            isRemovalOverlay ? styles.overlayRemovalBanner : styles.overlayBanner
          }
          role="status"
          aria-live="polite"
        >
          {overlaySummary}
        </div>
      )}

      {!isRemovalOverlay && (
        <>
          <div className={styles.formGroup}>
            <div className={styles.labelRow}>
              <label className={styles.label} htmlFor="allDayToggle">
                {t("records.editor.calendar.all_day_label", "All-day event")}
              </label>
              <label
                className={styles.toggle}
                aria-label={t(
                  "records.editor.calendar.toggle_all_day_aria",
                  "Toggle all-day event"
                )}
              >
                <input
                  type="checkbox"
                  id="allDayToggle"
                  role="switch"
                  aria-checked={displayState.isAllDay}
                  checked={displayState.isAllDay}
                  onChange={(e) => handleAllDayToggle(e.target.checked)}
                  className={styles.toggleInput}
                  disabled={hasOverlay}
                />
                <span className={styles.toggleSlider} aria-hidden="true"></span>
              </label>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              {t("records.editor.calendar.start_label", "Start")}
            </label>
            <div className={styles.datetimeGroup}>
              <input
                type="date"
                className={styles.dateInput}
                value={displayState.startDate}
                onChange={(e) => handleFieldChange("startDate", e.target.value)}
                disabled={hasOverlay}
              />
              {!displayState.isAllDay && (
                <input
                  type="time"
                  className={styles.timeInput}
                  value={displayState.startTime}
                  onChange={(e) => handleFieldChange("startTime", e.target.value)}
                  disabled={hasOverlay}
                />
              )}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              {t("records.editor.calendar.end_label", "End")}
            </label>
            <div className={styles.datetimeGroup}>
              <input
                type="date"
                className={styles.dateInput}
                value={displayState.endDate}
                onChange={(e) => handleFieldChange("endDate", e.target.value)}
                min={displayState.startDate}
                disabled={hasOverlay}
              />
              {!displayState.isAllDay && (
                <input
                  type="time"
                  className={styles.timeInput}
                  value={displayState.endTime}
                  onChange={(e) => handleFieldChange("endTime", e.target.value)}
                  disabled={hasOverlay}
                />
              )}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="timezone">
              {t("records.editor.calendar.timezone_label", "Timezone")}
            </label>
            <select
              id="timezone"
              className={styles.input}
              value={displayState.timezone}
              onChange={(e) => handleFieldChange("timezone", e.target.value)}
              disabled={hasOverlay}
            >
              <option value={defaultTimezone}>
                {t("records.editor.calendar.timezone_auto", {
                  timezone: defaultTimezone,
                  defaultValue: `Auto-detected (${defaultTimezone})`,
                })}
              </option>
              <option value="America/New_York">
                {t(
                  "records.editor.calendar.timezone_options.america_new_york",
                  "Eastern Time (America/New_York)"
                )}
              </option>
              <option value="America/Chicago">
                {t(
                  "records.editor.calendar.timezone_options.america_chicago",
                  "Central Time (America/Chicago)"
                )}
              </option>
              <option value="America/Denver">
                {t(
                  "records.editor.calendar.timezone_options.america_denver",
                  "Mountain Time (America/Denver)"
                )}
              </option>
              <option value="America/Los_Angeles">
                {t(
                  "records.editor.calendar.timezone_options.america_los_angeles",
                  "Pacific Time (America/Los_Angeles)"
                )}
              </option>
              <option value="UTC">UTC</option>
              <option value="Europe/London">
                {t(
                  "records.editor.calendar.timezone_options.europe_london",
                  "London (Europe/London)"
                )}
              </option>
              <option value="Europe/Paris">
                {t(
                  "records.editor.calendar.timezone_options.europe_paris",
                  "Paris (Europe/Paris)"
                )}
              </option>
              <option value="Asia/Tokyo">
                {t(
                  "records.editor.calendar.timezone_options.asia_tokyo",
                  "Tokyo (Asia/Tokyo)"
                )}
              </option>
              <option value="Asia/Shanghai">
                {t(
                  "records.editor.calendar.timezone_options.asia_shanghai",
                  "Shanghai (Asia/Shanghai)"
                )}
              </option>
              <option value="Australia/Sydney">
                {t(
                  "records.editor.calendar.timezone_options.australia_sydney",
                  "Sydney (Australia/Sydney)"
                )}
              </option>
            </select>
            {displayState.timezone !== defaultTimezone && (
              <div className={styles.timezoneNote}>
                {displayState.isAllDay
                  ? t("records.editor.calendar.timezone_note_all_day", "All day in ")
                  : t("records.editor.calendar.timezone_note_times", "Times shown in ")}
                {displayState.timezone}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
